import { Search } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatTimestamp } from "../../lib/format";
import { formatMxn, formatPhone, partnerStatusLabel } from "../../lib/loans";
import { usePartnerApplications } from "../../lib/queries";
import type { LoanStatus } from "../../types";
import { LoanStatusTag } from "../loan/LoanShared";

const FILTERS: Array<{ value: LoanStatus | ""; label: string }> = [
  { value: "", label: "Todas" }, { value: "submitted", label: "Nuevas" }, { value: "in_review", label: "En revisión" },
  { value: "approved", label: "Aprobadas" }, { value: "rejected", label: "Rechazadas" }
];

function isStatus(value: string | null): value is LoanStatus {
  return value === "submitted" || value === "in_review" || value === "approved" || value === "rejected";
}

/** The partner's queue: search by folio, name, e-mail or CURP; filter by status. */
export function PartnerApplicationsPage({ base }: { base: string }) {
  usePageHeader("Solicitudes de crédito", "«Viaja ahora, paga después» · enviadas desde Aventuria");
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const statusParam = params.get("estatus");
  const status: LoanStatus | "" = isStatus(statusParam) ? statusParam : "";
  const applications = usePartnerApplications(search.trim(), status);
  return (
    <div className="page-container">
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Folio, nombre, correo o CURP" aria-label="Buscar solicitudes" />
        </label>
        <div className="chip-row" role="tablist" aria-label="Filtrar por estatus">
          {FILTERS.map((filter) => (
            <button
              key={filter.value || "all"} type="button" role="tab" aria-selected={status === filter.value}
              className={`chip${status === filter.value ? " active" : ""}`}
              onClick={() => setParams(filter.value ? { estatus: filter.value } : {})}
            >{filter.label}</button>
          ))}
        </div>
        <span className="toolbar-count">{applications.data?.length ?? 0} solicitudes</span>
      </div>
      {applications.isLoading && <div className="page-state"><Spinner label="Cargando solicitudes" /></div>}
      {applications.data && (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Folio · Estatus</th><th>Solicitante</th><th>Contacto</th><th>CURP</th><th>Monto</th><th>Plazo</th><th>Enviada</th><th>Resuelta</th></tr>
            </thead>
            <tbody>
              {applications.data.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`${base}/solicitudes/${row.id}`} className="table-primary">
                      <strong translate="no">{row.folio}</strong>
                      <span><LoanStatusTag status={row.status} audience="partner" /></span>
                    </Link>
                  </td>
                  <td><Link to={`${base}/solicitudes/${row.id}`} className="table-primary"><strong>{row.applicant.fullName}</strong><span>{row.account.email !== row.applicant.email ? `Cuenta: ${row.account.email}` : "Cuenta Aventuria"}</span></Link></td>
                  <td><div className="table-primary"><span>{row.applicant.email}</span><span translate="no">{formatPhone(row.applicant.phone)}</span></div></td>
                  <td translate="no" className="mono">{row.curp}</td>
                  <td translate="no"><strong>{formatMxn(row.amount)}</strong></td>
                  <td>{row.termMonths} meses</td>
                  <td>{formatTimestamp(row.submittedAt)}</td>
                  <td>{row.decidedAt ? `${partnerStatusLabel(row.status)} · ${formatTimestamp(row.decidedAt)}` : "—"}</td>
                </tr>
              ))}
              {applications.data.length === 0 && <tr><td colSpan={8} className="table-empty">Sin solicitudes{status ? " con este estatus" : ""}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
